import { Box, Text, Skeleton } from "@mantine/core";
import type { ProviderViewModel } from "@models/provider";
import type { ModelBasicInfoViewModel, ModelViewModel } from "@models/model";
import { useTranslation } from "@hooks/useTranslation";
import { ProviderModelSelect } from "../ProviderModelSelect";
import { ConfigOptionsFields } from "../ConfigOptionsFields";
import "./LLMConfigTab.scss";

interface LLMConfigTabProps {
    isLoadingProviders: boolean;
    isLoadingModels?: boolean;
    providers: ProviderViewModel[];
    models: ModelBasicInfoViewModel[];
    selectedProvider: string;
    selectedModel: ModelViewModel | null;
    temperature: number | string;
    topP: number | string;
    topK: number | string;
    maxOutputTokens: number | string;
    thinkingBudget: number | string;
    seed: number | string;
    onProviderChange: (providerKey: string) => void;
    onModelChange: (modelName: string) => void;
    onTemperatureChange: (value: number | string) => void;
    onTopPChange: (value: number | string) => void;
    onTopKChange: (value: number | string) => void;
    onMaxOutputTokensChange: (value: number | string) => void;
    onThinkingBudgetChange: (value: number | string) => void;
    onSeedChange: (value: number | string) => void;
}

export const LLMConfigTab = ({
    isLoadingProviders,
    isLoadingModels,
    providers,
    models,
    selectedProvider,
    selectedModel,
    temperature,
    topP,
    topK,
    maxOutputTokens,
    thinkingBudget,
    seed,
    onProviderChange,
    onModelChange,
    onTemperatureChange,
    onTopPChange,
    onTopKChange,
    onMaxOutputTokensChange,
    onThinkingBudgetChange,
    onSeedChange,
}: LLMConfigTabProps) => {
    const { t } = useTranslation();

    if (isLoadingProviders) {
        return (
            <Box className="llm-config-tab__form">
                <Skeleton height={56} />
                <Skeleton height={56} />
                <Skeleton height={120} />
                <Skeleton height={56} />
                <Skeleton height={56} />
                <Skeleton height={56} />
                <Skeleton height={56} />
            </Box>
        );
    }

    return (
        <Box className="llm-config-tab__form">
            <ProviderModelSelect
                isLoadingProviders={isLoadingProviders}
                isLoadingModels={isLoadingModels}
                providers={providers}
                models={models}
                selectedProvider={selectedProvider}
                selectedModel={selectedModel}
                onProviderChange={onProviderChange}
                onModelChange={onModelChange}
            />

            <Text size="sm" fw={600} className="llm-config-tab__subtitle">
                {t("admin.flowConfigs.labels.llmConfig")}
            </Text>

            <ConfigOptionsFields
                temperature={temperature}
                topP={topP}
                topK={topK}
                maxOutputTokens={maxOutputTokens}
                thinkingBudget={thinkingBudget}
                seed={seed}
                onTemperatureChange={onTemperatureChange}
                onTopPChange={onTopPChange}
                onTopKChange={onTopKChange}
                onMaxOutputTokensChange={onMaxOutputTokensChange}
                onThinkingBudgetChange={onThinkingBudgetChange}
                onSeedChange={onSeedChange}
            />
        </Box>
    );
};
